import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import useConjointState from '@aytm/app/stats/page/question-views/cjc/states/useConjointState'
import useConjointExport from '@aytm/app/stats/page/question-views/cjc/states/useConjointExport'

import Head from '@aytm/app/stats/page/question-views/cjc/components/head/Head'
import Body from '@aytm/app/stats/page/question-views/cjc/components/Body'
import DataRun from '@aytm/app/stats/page/question-views/components/data-run/DataRun'
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'

// What the run panel says while the question has no report yet.
const calcRunNote = answer => {
  if (null == answer?.total) return ''

  return `${formatAmounts(answer, 'total')} ${1 === answer.total ? 'complete' : 'completes'} collected so far`
}

// Everything the researcher sees once the numbers are crunched: the question's own head, and -- for
// a question with more than one package to compare -- the shared body that draws them. The export
// handlers only this half needs are resolved here, so a question with nothing to draw never pays
// for them.
const BlockBody = ({ state, ...props }) => {
  const {
    answer,
    batches,
    chartLegendMode,
    colorTheme,
    components,
    designSheet,
    features,
    fullViewOnly,
    globalChartMode,
    globalZoomLevel,
    hideTotal,
    isAnimated,
    link,
    position,
    processText,
    questionChartMode,
    questionId,
    questionKey,
    questionType,
    visualPosition,
    onChartExport,
    onChartReady,
    onDesignExport,
    onExport,
    onMarketSimulatorExport
  } = props

  // Signed in, the feature on, and numbers to export: all three the body already holds.
  const showExport = 'loading' !== answer && Boolean(PageGlobals.USER_ID) && !features?.export?.disabled

  const { onHeadExport, onSimulatorExport } = useConjointExport({
    state,
    answer,
    batches,
    designSheet,
    link,
    position,
    questionId,
    questionKey,
    questionType,
    visualPosition,
    onExport,
    onDesignExport,
    onMarketSimulatorExport
  })

  return (
    <>
      <Head
        isLite={state.isLiteView}
        isSummaryChartView={state.isSummaryChartView}
        isAwaitingFullSlice={state.isAwaitingFullSlice}
        showExport={showExport}
        fullViewOnly={fullViewOnly}
        onExport={onHeadExport}
        onShowBar={state.onShowBar}
        onShowVisual={state.onShowVisual}
      />
      {1 < (batches?.length || 0) ? (
        <Body
          {...state}
          answer={answer}
          batches={batches}
          questionType={questionType}
          questionId={questionId}
          questionKey={questionKey}
          components={components}
          features={features}
          colorTheme={colorTheme}
          chartLegendMode={chartLegendMode}
          questionChartMode={questionChartMode}
          globalChartMode={globalChartMode}
          globalZoomLevel={globalZoomLevel}
          hideTotal={hideTotal}
          isAnimated={isAnimated}
          processText={processText}
          onChartExport={onChartExport}
          onChartReady={onChartReady}
          onMarketSimulatorExport={onSimulatorExport}
        />
      ) : null}
    </>
  )
}

BlockBody.displayName = 'ConjointExpressBlockBody'

// Conjoint Express: the run panel, its head, and the shared body once there is more than one
// package to compare. block.coffee owns the mount and the report-param write; every value the
// block draws is resolved here, and the intents it reports go up through the api it hands over.
const Block = props => {
  const {
    answer,
    batches,
    calcBadData,
    defaultChartMode,
    defaultSortOrder,
    fullViewOnly,
    globalChartMode,
    globalZoomLevel,
    params,
    paramsRevision,
    questionKey,
    questionType,
    schemaKey,
    onBadData,
    onReady,
    onStateChange
  } = props

  // the question's own rule, applied to the payload on screen -- read here rather than taken as a
  // flag, so what the block draws can never disagree with what it was handed
  const badData = Boolean(calcBadData?.(answer))

  // the api the block hands up is owed on mount, so the state lives here rather than in the body
  const state = useConjointState({
    answer,
    batches,
    questionType,
    badData,
    defaultSortOrder,
    defaultChartMode,
    globalChartMode,
    globalZoomLevel,
    fullViewOnly,
    params,
    paramsRevision,
    onReady,
    onStateChange
  })

  return (
    <BlockWrapper answer={answer}>
      <DataRun
        answer={answer}
        badData={badData}
        type={questionType}
        questionKey={questionKey}
        schemaKey={schemaKey}
        note={calcRunNote(answer)}
        calcBadData={calcBadData}
        onBadData={onBadData}
      />
      {/* while the numbers have not been crunched there is nothing else to show */}
      {badData ? null : <BlockBody {...props} state={state} />}
    </BlockWrapper>
  )
}

Block.displayName = 'ConjointExpressBlock'

Block.propTypes = {
  ...Body.propTypes,
  answer: answerPropType,
  calcBadData: PropTypes.func,
  defaultChartMode: PropTypes.string,
  defaultSortOrder: PropTypes.string,
  params: PropTypes.object,
  // bumped by the shell every time it re-reads the params off the model
  paramsRevision: PropTypes.number,
  schemaKey: PropTypes.string,
  fullViewOnly: PropTypes.bool,
  designSheet: PropTypes.object,
  link: PropTypes.string,
  position: PropTypes.number,
  visualPosition: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onBadData: PropTypes.func,
  onDesignExport: PropTypes.func,
  onExport: PropTypes.func,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func
}

// the body is handed the block's own props, plus the state the block owns
BlockBody.propTypes = { ...Block.propTypes, state: PropTypes.object }

export default Block
